import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, DeleteConfirmation, FiltersBar, Icon, Page, SearchInput } from '~/components';
import { useDocumentTitle, useSearchParams, useSessionStorage } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import NavBox from './components/NavBox';
import NavSection from './components/NavSection';
import { useReports } from './ReportsContext';
import ReportForm from './ReportForm';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { sortAlphabetically } from '~/utils';

const AccordionButton = styled(Button)`
  font-size: 0.75rem;
  color: ${colors.grey55};
  opacity: 0.5;

  &:hover {
    color: ${colors.grey55};
    opacity: 1;
  }
`;

const NoResults = styled.div`
  color: ${colors.grey40};
  font-size: 0.875rem;
  margin-top: 1.5rem;
`;

export default function MyReportsPage() {
  useDocumentTitle('My Reports');

  const reportsContext = useReports();

  const sessionStorage = useSessionStorage({ key: 'my_reports__sections' });

  const [sections, setSections] = useState(() => {
    const sections = {
      favorites: true,
      saved: true,
    };
    try {
      if (sessionStorage.hasValue()) {
        const value = JSON.parse(sessionStorage.get());
        if (value) {
          sections.favorites = !!value.favorites;
          sections.saved = !!value.saved;
        }
      }
    } finally {
      sessionStorage.set(JSON.stringify(sections));
    }
    return sections;
  });

  const all = Object.keys(sections).every((section) => sections[section]);

  const toggleSection = (section) => {
    const value = { ...sections, [section]: !sections[section] };
    setSections(value);
    sessionStorage.set(JSON.stringify(value));
  };

  const toggleAllSections = () => {
    const value = {
      favorites: !all,
      saved: !all,
    };

    setSections(value);
    sessionStorage.set(JSON.stringify(value));
  };

  const [dialog, setDialog] = useState(null);
  const [params, setParams] = useState(() => ({ q: '' }));
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(() => ({ q: { default: '' } }), []),
    sessionKey: 'my_reports__params',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });
  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      if (params) {
        setParams((state) => ({ ...state, ...params }));
        setSearchParamsStatus('ready');
      }
    });
  }, [searchParams, searchParamsStatus]);

  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const confirmation = useConfirmation();
  const history = useHistory();

  const config = useMemo(() => {
    if (!reportsContext?.reports || searchParamsStatus !== 'ready') return null;

    const filter = (reports) => {
      let results = sortAlphabetically(reports, ['name']);

      if (params.q) {
        const fuse = new Fuse(reports, {
          keys: ['name', 'description', 'section'],
          threshold: 0.1,
          ignoreLocation: true,
        });

        results = fuse.search(params.q).map(({ item }) => item);
      }

      return results;
    };

    const sections = [
      { id: 'favorites', title: 'Favorites', reports: filter(reportsContext.reports?.filter((r) => r.isFavorite)) },
      { id: 'saved', title: 'Saved', reports: filter(reportsContext.reports?.filter((r) => r.scopeKey === 'member')) },
    ];

    return sections.filter(({ reports }) => reports.length);
  }, [reportsContext?.reports, params.q, searchParamsStatus]);

  const handleFilter = ({ target: { name, value } }) => {
    setParams((params) => ({ ...params, [name]: value }));
    searchParams.set({ [name]: value });
  };

  const handleCloseDialog = () => {
    setDialog(null);
  };

  const handleEdit = async (report) => {
    setDialog(
      <ReportForm
        title="Edit Report"
        report={report}
        search={report.params}
        onClose={handleCloseDialog}
        onSaved={async () => {
          handleCloseDialog();
        }}
        onDelete={() => handleDelete(report)}
      />,
    );
  };

  const handleSaveAs = async (report) => {
    setDialog(
      <ReportForm
        title="Save Report As"
        report={{
          name: report.name,
          description: report.description,
          generatorKey: report.generatorKey,
          templateKey: report.templateKey,
          params: report.params,
        }}
        search={report.params}
        onClose={handleCloseDialog}
        onSaved={async () => {
          handleCloseDialog();
          history.replace(`/app/${workspace.key}/reports/my-reports`);
        }}
      />,
    );
  };

  const handleDelete = async (report) => {
    await confirmation.prompt((resolve) => {
      return (
        <DeleteConfirmation
          title="Delete report"
          resolve={async (confirm) => {
            if (confirm) {
              await api.www.workspaces(workspace.id).reports(report.id).remove();
              await reportsContext.fetchData();
              toast.success('Report deleted.');
            }
            resolve(true);
          }}>
          Are you sure you want to delete the report "{report.name}"?
        </DeleteConfirmation>
      );
    });
  };

  return (
    <Page>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Reports</Page.Eyebrow>
          <Page.Title>
            My Reports{' '}
            <AccordionButton isAnchor onClick={() => toggleAllSections()}>
              <Icon icon={all ? 'angle-double-down' : 'angle-double-up'} />
            </AccordionButton>
          </Page.Title>
        </Page.Info>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <div style={{ gridColumn: '1 / 3' }}>
            <SearchInput name="q" value={params.q} onChange={handleFilter} placeholder="Search" autoFocus />
          </div>
        </FiltersBar>
      </Page.Filters>

      {!config ? (
        <PageLoader />
      ) : (
        <>
          {config.map((section) => (
            <NavSection
              key={section.id}
              title={section.title}
              expanded={sections[section.id]}
              onToggle={() => toggleSection(section.id)}>
              {_.map(section.reports, (report) => {
                const handleAction = (action) => (e) => {
                  e.preventDefault();
                  action(report);
                };

                return (
                  <NavBox
                    key={report.id ?? report.key}
                    area="my-reports"
                    report={report}
                    onEdit={handleAction(handleEdit)}
                    onSaveAs={handleAction(handleSaveAs)}
                    onDelete={handleAction(handleDelete)}
                  />
                );
              })}
            </NavSection>
          ))}

          {!config.length && <NoResults>No results found.</NoResults>}
        </>
      )}
      {dialog}
    </Page>
  );
}
